import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoized logout function
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    // 1. Initial authentication check on application mount
    const currentUser = authService.getCurrentUser();
    const token = authService.getToken ? authService.getToken() : localStorage.getItem('token');
    
    let expTime = null;
    if (token) {
      try {
        // Decode payload from JWT (base64)
        const payload = JSON.parse(atob(token.split('.')[1]));
        expTime = payload.exp ? payload.exp * 1000 : null;
      } catch (err) {
        console.error('Failed to parse JWT token on mount', err);
      }
    }

    // If token exists but is already expired on initial load, log out immediately
    if (currentUser && expTime && expTime <= Date.now()) {
      logout();
    } else {
      setUser(currentUser);
    }
    
    setLoading(false);

    // 2. Listen for 'auth:unauthorized' events emitted by Axios interceptor (Strategy 2)
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [logout]);

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    setUser(response.data.user || response.data);
    return response;
  };

  const loginWithPhone = async (phone) => {
    const response = await authService.firebasePhoneLogin(phone);
    setUser(response.data.user || response.data);
    return response;
  };

  const register = async (name, email, password) => {
    const response = await authService.register(name, email, password);
    setUser(response.data.user || response.data);
    return response;
  };

  const value = {
    user,
    login,
    loginWithPhone,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isVendor: user?.role === 'vendor',
    isAdminOrVendor: user?.role === 'admin' || user?.role === 'vendor'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};